import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { allComics } from '@/mocks/comics';

const BASE_URL = 'https://hanman.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(slug || 'recommend');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.id === activeCategory) || categories[0];
  const categoryComics = activeCategory === 'recommend'
    ? allComics
    : activeCategory === 'complete'
      ? allComics.filter((c) => c.status === '完结')
      : allComics.filter((c) => c.category === activeCategory);

  const displayComics = categoryComics.length > 0 ? categoryComics : allComics.slice(0, 4);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 韩漫网`,
    description: currentCategory.description,
    url: `${BASE_URL}/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: BASE_URL,
    },
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name}_好看的韩国漫画_韩漫在线观看-韩漫网`}
        description={`韩漫网${currentCategory.name}分类，${currentCategory.description}。免费韩漫在线阅读，漫画网址首选。共${currentCategory.count}部作品等您发现。`}
        keywords={`${currentCategory.name},韩国漫画,韩漫在线观看,免费韩漫,韩漫网`}
        canonicalUrl={`${BASE_URL}/category/${activeCategory}`}
        ogType="website"
        schema={schema}
      />

      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
            {currentCategory.name}
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            {currentCategory.description} · 共 {currentCategory.count} 部作品
          </p>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {displayComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}